import { Pipe, PipeTransform } from '@angular/core';
import { OrderDetailField, StatusToBadgeMapping } from '@vendasta/sales-ui';
import {
  CustomerApprovalEventBounced,
  CustomerApprovalEventClicked,
  CustomerApprovalEventDeferred,
  CustomerApprovalEventDelivered,
  CustomerApprovalEventDropped,
  CustomerApprovalEventOpened,
  CustomerApprovalEventProcessing,
  CustomerApprovalEventSending,
  CustomerApprovalEventSpamReport,
  CustomerApprovalEventUnsubscribed,
  CustomerApprovalEventUpdatedAnswers,
  DeclineCancellationEvent,
  RequestCancellationEvent,
  Status,
  UpdateContractDurationEvent,
  UpdatedCustomerNotesEvent,
  UpdatedDraftEvent,
  UpdatedLineItemsEvent,
  UpdatedNotesEvent,
  UpdatedRequestedActivationEvent,
  UpdatedSalespersonEvent,
  UpdatedTagsEvent,
  UpdateEnforceContractTermEvent,
  UpdatedCustomerAttachmentsEvent,
  UpdatedAttachmentsEvent,
} from '@vendasta/sales-orders';

@Pipe({ standalone: true, name: 'buildDetailName' })
export class BuildDetailNamePipe implements PipeTransform {
  transform(orderDetailField: OrderDetailField, isChild: boolean): string {
    if (isChild) {
      return this.getEventNameTranslationKeyForOrderDetailField(orderDetailField);
    }
    // Not all sales histories were created with a status
    return StatusToBadgeMapping.get(orderDetailField.status)?.text || StatusToBadgeMapping.get(Status.SUBMITTED).text;
  }

  getEventNameTranslationKeyForOrderDetailField(orderDetailField: OrderDetailField): string {
    if (!orderDetailField || !orderDetailField.metadata) {
      return null;
    }

    if (orderDetailField.metadata.event.id === UpdatedTagsEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_TAGS';
    }

    if (orderDetailField.metadata.event.id === UpdatedNotesEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_NOTES';
    }

    if (orderDetailField.metadata.event.id === UpdatedSalespersonEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_SALESPERSON';
    }

    if (orderDetailField.metadata.event.id === UpdatedCustomerNotesEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_CUSTOMER_NOTES';
    }

    if (orderDetailField.metadata.event.id === UpdatedRequestedActivationEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_CONTRACT_START';
    }

    if (orderDetailField.metadata.event.id === UpdateContractDurationEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_CONTRACT_LENGTH';
    }

    if (orderDetailField.metadata.event.id === UpdateEnforceContractTermEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_ENFORCE_CONTRACT_TERM';
    }

    if (orderDetailField.metadata.event.id === RequestCancellationEvent) {
      return 'FRONTEND.SALES_UI.CANCELLATION_REQUESTED';
    }

    if (orderDetailField.metadata.event.id === DeclineCancellationEvent) {
      return 'FRONTEND.SALES_UI.CANCELLATION_DECLINED';
    }

    if (orderDetailField.metadata.event.id === UpdatedDraftEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_DRAFT';
    }

    if (orderDetailField.metadata.event.id === UpdatedLineItemsEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_CONTENTS';
    }

    if (orderDetailField.metadata.event.id === UpdatedCustomerAttachmentsEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_CUSTOMER_ATTACHMENTS';
    }

    if (orderDetailField.metadata.event.id === UpdatedAttachmentsEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_ADMIN_ATTACHMENTS';
    }

    if (orderDetailField.status !== Status.SUBMITTED_FOR_CUSTOMER_APPROVAL) {
      return null;
    }
    return this.getCustomerApprovalTranslationKeyForEventID(orderDetailField.metadata.event.id);
  }

  getCustomerApprovalTranslationKeyForEventID(eventID: string): string {
    switch (eventID) {
      case CustomerApprovalEventSending:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.SENDING';
      case CustomerApprovalEventProcessing:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.PROCESSING';
      case CustomerApprovalEventDelivered:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.DELIVERED';
      case CustomerApprovalEventBounced:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.BOUNCED';
      case CustomerApprovalEventDropped:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.DROPPED';
      case CustomerApprovalEventUnsubscribed:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.UNSUBSCRIBED';
      case CustomerApprovalEventDeferred:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.DEFERRED';
      case CustomerApprovalEventSpamReport:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.SPAM_REPORT';
      case CustomerApprovalEventOpened:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.EMAIL_OPENED';
      case CustomerApprovalEventClicked:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.EMAIL_CLICKED';
      case CustomerApprovalEventUpdatedAnswers:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.UPDATED_ANSWERS';
      default:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.UNKNOWN';
    }
  }
}
